import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldCheck, Plus, MoreHorizontal } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Badge,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import {
  useSecgroups,
  useCreateSecgroup,
  useUpdateSecgroup,
  useDeleteSecgroup,
  ruleSizeForPayload,
  type Secgroup,
  type SecgroupRule,
} from '@/api/opennebula';
import { SecgroupRuleEditor } from './components/SecgroupRuleEditor';

const DEFAULT_RULES: SecgroupRule[] = [
  { rule_type: 'INBOUND', protocol: 'TCP', range: '22', ip: '' },
];

export function SecurityGroups() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { data: groups = [], isLoading } = useSecgroups(serviceId);
  const [editing, setEditing] = useState<Secgroup | null | 'new'>(null);
  const [del, setDel] = useState<Secgroup | null>(null);

  const remove = useDeleteSecgroup(serviceId!, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Security group deleted' });
      setDel(null);
    },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  const columns: ColumnDef<Secgroup, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</span> },
    {
      id: 'inbound',
      header: 'Inbound',
      cell: ({ row }) => (
        <Badge tone="info" appearance="subtle" size="sm">
          {(row.original.rules ?? []).filter((r) => r.rule_type === 'INBOUND').length} rules
        </Badge>
      ),
    },
    {
      id: 'outbound',
      header: 'Outbound',
      cell: ({ row }) => (
        <Badge tone="neutral" appearance="subtle" size="sm">
          {(row.original.rules ?? []).filter((r) => r.rule_type === 'OUTBOUND').length} rules
        </Badge>
      ),
    },
    {
      id: 'attached',
      header: 'Attached',
      cell: ({ row }) => {
        const n = row.original.attached?.length ?? 0;
        return n ? <Badge tone="success" appearance="subtle" size="sm">{n} VM{n === 1 ? '' : 's'}</Badge> : <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton variant="ghost" size="sm" aria-label="Security group actions">
                <MoreHorizontal size={16} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setEditing(row.original)}>Edit</DropdownMenuItem>
              <DropdownMenuItem tone="danger" onSelect={() => setDel(row.original)}>
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <Container size="xl">
      <PageHeader
        icon={<ShieldCheck size={22} />}
        title="Security groups"
        description="Inbound and outbound firewall rule sets you can attach to instances."
        actions={
          <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setEditing('new')}>
            New security group
          </Button>
        }
      />
      {/* Empty placeholder renders inside the table's own bordered frame
          (via `emptyState`), so the zone keeps the same frame whether empty or full. */}
      {isLoading ? null : (
        <DataTable
          columns={columns}
          data={groups}
          getRowId={(g) => String(g.id)}
          emptyState={
            <EmptyState
              icon={<ShieldCheck size={28} />}
              title="No security groups"
              description="Create a rule set to control instance traffic."
              actions={
                <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setEditing('new')}>
                  New security group
                </Button>
              }
            />
          }
        />
      )}

      {editing !== null && (
        <SecgroupDialog
          serviceId={serviceId!}
          group={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title="Delete security group?"
        description={`Remove “${del?.name || del?.id}”? Instances using it will lose these rules.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => del && remove.mutate(del.id)}
      />
    </Container>
  );
}

function SecgroupDialog({
  serviceId,
  group,
  onClose,
}: {
  serviceId: string;
  group: Secgroup | null;
  onClose: () => void;
}) {
  const { show } = useToast();
  const [name, setName] = useState(group?.name ?? '');
  const [rules, setRules] = useState<SecgroupRule[]>(group?.rules ?? DEFAULT_RULES);

  useEffect(() => {
    setName(group?.name ?? '');
    setRules(group?.rules ?? DEFAULT_RULES);
  }, [group]);

  const create = useCreateSecgroup(serviceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Security group created' });
      onClose();
    },
    onError: () => show({ tone: 'danger', title: 'Create failed' }),
  });
  const update = useUpdateSecgroup(serviceId, group?.id ?? '', {
    onSuccess: () => {
      show({ tone: 'success', title: 'Security group updated' });
      onClose();
    },
    onError: () => show({ tone: 'danger', title: 'Update failed' }),
  });

  const pending = create.isPending || update.isPending;
  const save = () => {
    // Legacy payload splits rules into inbound/outbound arrays; each rule needs a
    // ref_type (preset name, default 'Custom') + string range/ip.
    const norm = (r: SecgroupRule) => {
      const ip = r.ip ?? '';
      return {
        ref_type: r.ref_type || 'Custom',
        rule_type: r.rule_type,
        protocol: r.protocol,
        range: r.range ?? '',
        ip,
        ...ruleSizeForPayload(ip, r.size),
        ...(r.icmp_type ? { icmp_type: r.icmp_type } : {}),
      };
    };
    const payload = {
      name: name.trim(),
      inbound: rules.filter((r) => r.rule_type === 'INBOUND').map(norm),
      outbound: rules.filter((r) => r.rule_type === 'OUTBOUND').map(norm),
    };
    if (group) update.mutate(payload);
    else create.mutate(payload);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{group ? 'Edit security group' : 'New security group'}</DialogTitle>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Field label="Name">
            {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder="web-servers" />}
          </Field>
          <SecgroupRuleEditor value={rules} onChange={setRules} />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!name.trim() || pending} onClick={save}>
            {group ? 'Save changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
